import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import SelectSymbol from '../../components/SelectSymbol';
import useWebSocket from 'react-use-websocket';
import { getSymbol, getSymbols } from '../../services/SymbolsService';
import { getMemoryIndex } from '../../services/BeholderService';
import { parseWalletQty } from '../../components/WalletSummary';
import { placeOrder } from '../../services/OrdersService';
import { getCriptoInfo } from '../../services/AgentService';
import { getBalance } from '../../services/ExchangeService';
import './Trade.css';

function PriceDisplay({ symbol, onPriceChange }) {
    const [book, setBook] = useState({ bid: '0', ask: '0' });
    const onPriceChangeRef = useRef(onPriceChange);
    onPriceChangeRef.current = onPriceChange;

    function getBinanceWSUrl() {
        if (!symbol) return null;
        const base = String(process.env.REACT_APP_BWS_URL || 'wss://stream.binance.com:9443/ws').replace(/\/+$/, '');
        return `${base}/${symbol.toLowerCase()}@bookTicker`;
    }

    const { lastJsonMessage } = useWebSocket(getBinanceWSUrl(), {
        onError: (event) => console.error(event),
        shouldReconnect: () => true,
        reconnectInterval: 3000
    }, Boolean(symbol));

    useEffect(() => {
        const ticker = lastJsonMessage?.data || lastJsonMessage;
        if (!ticker || (ticker.b == null && ticker.a == null)) return;
        const b = { bid: String(ticker.b), ask: String(ticker.a) };
        setBook(b);
        if (onPriceChangeRef.current) onPriceChangeRef.current(b);
    }, [lastJsonMessage]);

    return (
        <div className="price-display">
            <div className="price-row">
                <div className="price-item">
                    <span className="price-label">Compra (BID)</span>
                    <span className="price-value buy-price">{parseFloat(book.bid).toFixed(8)}</span>
                </div>
                <div className="price-item">
                    <span className="price-label">Venda (ASK)</span>
                    <span className="price-value sell-price">{parseFloat(book.ask).toFixed(8)}</span>
                </div>
            </div>
        </div>
    );
}

function Trade() {
    const navigate = useNavigate();
    
    const [symbol, setSymbol] = useState('');
    const [symbolData, setSymbolData] = useState(null);
    const [side, setSide] = useState('BUY'); // BUY ou SELL
    const [quantity, setQuantity] = useState('');
    const [price, setPrice] = useState('0');
    const [total, setTotal] = useState('0');
    const [wallet, setWallet] = useState({ base: { symbol: '', qty: 0 }, quote: { symbol: '', qty: 0 } });
    const [cryptoInfo, setCryptoInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [orderType, setOrderType] = useState('MARKET'); // MARKET ou LIMIT
    const [limitPrice, setLimitPrice] = useState('');
    const [availableSymbols, setAvailableSymbols] = useState([]);
    const [userBalance, setUserBalance] = useState({});

    // Carrega o saldo do usuário quando necessário
    useEffect(() => {
        if (side === 'SELL') {
            // Recarrega o saldo quando muda para vender
            loadUserBalance();
        }
    }, [side]);

    // Filtra símbolos quando muda entre comprar/vender ou quando o saldo é carregado
    useEffect(() => {
        if (side === 'SELL') {
            // Para vender, só filtra se tiver saldo carregado
            if (Object.keys(userBalance).length > 0) {
                filterAvailableSymbols();
            }
        } else {
            // Para comprar, limpa a lista de símbolos filtrados
            setAvailableSymbols([]);
        }
    }, [side, userBalance]);

    async function loadUserBalance() {
        try {
            const balance = await getBalance(false);
            // Converte para objeto simples { COIN: { available: X } }
            const balanceObj = {};
            if (Array.isArray(balance)) {
                balance.forEach(item => {
                    balanceObj[item.asset || item.symbol] = {
                        available: item.available || item.availableBalance || 0
                    };
                });
            } else {
                Object.entries(balance).forEach(([key, value]) => {
                    if (key !== 'fiatEstimate') {
                        balanceObj[key] = {
                            available: value.available || value.availableBalance || 0
                        };
                    }
                });
            }
            setUserBalance(balanceObj);
        } catch (err) {
            console.error('Erro ao carregar saldo:', err);
            setUserBalance({});
        }
    }

    async function filterAvailableSymbols() {
        try {
            if (side === 'SELL') {
                // Para vender, mostra apenas símbolos onde o usuário tem saldo na moeda base
                if (Object.keys(userBalance).length === 0) {
                    // Se ainda não carregou o saldo, aguarda
                    setAvailableSymbols([]);
                    return;
                }
                
                const allSymbols = await getSymbols();
                const symbolList = allSymbols.rows || allSymbols;
                
                // Obtém as moedas base que o usuário possui (com saldo > 0)
                const ownedBaseCoins = Object.entries(userBalance)
                    .filter(([coin, data]) => parseFloat(data.available || 0) > 0)
                    .map(([coin]) => coin);
                
                if (ownedBaseCoins.length === 0) {
                    setAvailableSymbols([]);
                    return;
                }
                
                // Prioridade de quotes: USDC > USDT > BUSD > outros
                const quotePriority = ['USDC', 'USDT', 'BUSD'];
                
                // Para cada moeda base que o usuário possui, encontra o melhor par disponível
                const availablePairs = [];
                
                ownedBaseCoins.forEach(baseCoin => {
                    // Busca todos os pares disponíveis para esta moeda base
                    const pairsForBase = symbolList.filter(s => s.base === baseCoin);
                    
                    if (pairsForBase.length === 0) return;
                    
                    // Ordena por prioridade de quote
                    const sortedPairs = pairsForBase.sort((a, b) => {
                        const aPriority = quotePriority.indexOf(a.quote) !== -1 
                            ? quotePriority.indexOf(a.quote) 
                            : 999;
                        const bPriority = quotePriority.indexOf(b.quote) !== -1 
                            ? quotePriority.indexOf(b.quote) 
                            : 999;
                        return aPriority - bPriority;
                    });
                    
                    // Adiciona apenas o primeiro (melhor) par para esta moeda base
                    if (sortedPairs.length > 0) {
                        availablePairs.push(sortedPairs[0].symbol);
                    }
                });
                
                setAvailableSymbols(availablePairs);
            } else {
                // Para comprar, não filtra (mostra todos)
                setAvailableSymbols([]);
            }
        } catch (err) {
            console.error('Erro ao filtrar símbolos:', err);
            setAvailableSymbols([]);
        }
    }

    useEffect(() => {
        if (!symbol) return;
        
        setLoading(true);
        getSymbol(symbol)
            .then(data => {
                setSymbolData(data);
                setLimitPrice('');
                loadWallet(data);
                loadCryptoInfo(data.base);
            })
            .catch(err => {
                console.error('Erro ao carregar símbolo:', err);
                let errorMsg = 'Erro ao carregar símbolo';
                if (err && err.response && err.response.data) {
                    const data = err.response.data;
                    if (typeof data === 'string') {
                        errorMsg = data;
                    } else if (data.msg) {
                        errorMsg = data.msg;
                    } else if (data.message) {
                        errorMsg = data.message;
                    }
                } else if (err && err.message) {
                    errorMsg = err.message;
                }
                setError(errorMsg);
            })
            .finally(() => setLoading(false));
    }, [symbol]);

    async function loadWallet(symbolObj) {
        try {
            const baseQty = parseWalletQty(await getMemoryIndex(symbolObj.base, 'WALLET', null));
            const quoteQty = parseWalletQty(await getMemoryIndex(symbolObj.quote, 'WALLET', null));
            setWallet({ 
                base: { qty: baseQty, symbol: symbolObj.base }, 
                quote: { qty: quoteQty, symbol: symbolObj.quote } 
            });
        } catch (err) {
            console.error(err);
        }
    }

    async function loadCryptoInfo(baseSymbol) {
        try {
            const info = await getCriptoInfo(baseSymbol);
            if (info && info.image) {
                setCryptoInfo(info);
            }
        } catch (err) {
            console.error(err);
        }
    }

    function onSymbolChange(event) {
        const newSymbol = event.target.value;
        setSymbol(newSymbol);
        setQuantity('');
        setPrice('0');
        setTotal('0');
        setError('');
        
        // Se mudou o símbolo e está em modo vender, verifica se ainda tem saldo
        if (side === 'SELL' && newSymbol) {
            getSymbol(newSymbol).then(data => {
                loadWallet(data);
            });
        }
    }

    function handleSideChange(newSide) {
        setSide(newSide);
        // Limpa o símbolo quando muda entre comprar/vender para forçar nova seleção
        if (symbol) {
            setSymbol('');
            setSymbolData(null);
            setQuantity('');
            setPrice('0');
            setTotal('0');
            setError('');
        }
    }

    function onPriceChange(book) {
        if (book) {
            const currentPrice = side === 'BUY' ? parseFloat(book.ask) : parseFloat(book.bid);
            setPrice(currentPrice.toString());
            
            if (orderType === 'MARKET' && quantity) {
                calculateTotal(currentPrice);
            }
        }
    }

    function calculateTotal(priceValue) {
        if (!quantity || !priceValue) {
            setTotal('0');
            return;
        }
        
        const qty = parseFloat(quantity.replace(',', '.'));
        const totalValue = qty * priceValue;
        setTotal(totalValue.toFixed(8));
        
        // Validações
        if (symbolData) {
            if (!orderType || orderType === 'MARKET') {
                // Validação para MARKET
                if (side === 'BUY' && totalValue > parseFloat(wallet.quote.qty)) {
                    setError(`Saldo insuficiente. Você tem ${wallet.quote.qty} ${symbolData.quote}`);
                } else if (side === 'SELL' && qty > parseFloat(wallet.base.qty)) {
                    setError(`Saldo insuficiente. Você tem ${wallet.base.qty} ${symbolData.base}`);
                } else if (totalValue < parseFloat(symbolData.minNotional)) {
                    setError(`Valor mínimo: ${symbolData.minNotional}`);
                } else if (qty < parseFloat(symbolData.minLotSize)) {
                    setError(`Quantidade mínima: ${symbolData.minLotSize}`);
                } else {
                    setError('');
                }
            } else {
                // Validação para LIMIT
                const limitPriceValue = parseFloat(limitPrice);
                if (!limitPriceValue) {
                    setError('Informe o preço limite');
                } else {
                    const totalLimit = qty * limitPriceValue;
                    if (side === 'BUY' && totalLimit > parseFloat(wallet.quote.qty)) {
                        setError(`Saldo insuficiente. Você tem ${wallet.quote.qty} ${symbolData.quote}`);
                    } else if (side === 'SELL' && qty > parseFloat(wallet.base.qty)) {
                        setError(`Saldo insuficiente. Você tem ${wallet.base.qty} ${symbolData.base}`);
                    } else if (totalLimit < parseFloat(symbolData.minNotional)) {
                        setError(`Valor mínimo: ${symbolData.minNotional}`);
                    } else if (qty < parseFloat(symbolData.minLotSize)) {
                        setError(`Quantidade mínima: ${symbolData.minLotSize}`);
                    } else {
                        setError('');
                    }
                }
            }
        }
    }

    useEffect(() => {
        if (orderType === 'MARKET' && price && quantity) {
            calculateTotal(parseFloat(price));
        } else if (orderType === 'LIMIT' && limitPrice && quantity) {
            calculateTotal(parseFloat(limitPrice));
        }
    }, [quantity, price, limitPrice, orderType, side, symbolData]);

    function handleQuantityChange(e) {
        const value = e.target.value;
        setQuantity(value);
    }

    function handleLimitPriceChange(e) {
        const value = e.target.value;
        setLimitPrice(value);
    }

    function roundToStep(qty, stepSize) {
        const amount = parseFloat(qty);
        const step = parseFloat(stepSize);
        if (!Number.isFinite(amount) || amount <= 0) return 0;
        if (!Number.isFinite(step) || step <= 0) return amount;
        const rounded = Math.floor((amount + 1e-12) / step) * step;
        const decimals = (String(step).split('.')[1] || '').replace(/0+$/, '').length;
        return parseFloat(rounded.toFixed(Math.max(decimals, 0)));
    }

    function maxSellableFromWallet() {
        const available = parseFloat(userBalance[symbolData?.base]?.available || wallet.base.qty || 0);
        if (!(available > 0)) return 0;
        let maxQty = available;
        if (symbolData?.base === 'BNB') {
            const step = parseFloat(symbolData.stepSize || 0) || 0;
            maxQty = Math.max(0, available - Math.max(available * 0.001, step));
        }
        return roundToStep(maxQty, symbolData?.stepSize);
    }

    function applyPercentage(percentage) {
        if (!symbolData) return;
        
        if (side === 'BUY') {
            const available = parseFloat(wallet.quote.qty);
            const amount = (available * percentage / 100);
            const qty = amount / parseFloat(price || limitPrice || 1);
            setQuantity(qty.toFixed(8));
        } else {
            const maxQty = maxSellableFromWallet();
            const qty = roundToStep(maxQty * percentage / 100, symbolData.stepSize);
            setQuantity(qty > 0 ? qty.toFixed(8) : '0');
        }
    }

    async function handleSubmit() {
        if (!symbol || !quantity) {
            setNotification({ type: 'error', text: 'Preencha todos os campos obrigatórios' });
            return;
        }

        if (error) {
            setNotification({ type: 'error', text: error });
            return;
        }

        let orderQty = parseFloat(quantity);

        // Validação adicional para venda
        if (side === 'SELL') {
            if (!symbolData) {
                setNotification({ type: 'error', text: 'Símbolo inválido' });
                return;
            }
            
            const baseSymbol = symbolData.base;
            const available = parseFloat(userBalance[baseSymbol]?.available || wallet.base.qty || 0);
            const maxQty = maxSellableFromWallet();
            
            if (orderQty > available || (maxQty > 0 && orderQty > maxQty + 1e-12)) {
                orderQty = maxQty;
                setQuantity(maxQty.toFixed(8));
            }
            
            if (orderQty <= 0) {
                setNotification({ type: 'error', text: 'Quantidade deve ser maior que zero' });
                return;
            }
        }

        setLoading(true);
        setError('');

        try {
            // Prepara a ordem conforme esperado pelo backend
            const order = {
                symbol: symbol.toUpperCase(),
                side: side,
                quantity: orderQty,
                options: {
                    type: orderType
                }
            };

            // Para ordens LIMIT, adiciona limitPrice
            if (orderType === 'LIMIT') {
                const priceValue = limitPrice || price;
                if (!priceValue || parseFloat(priceValue) <= 0) {
                    setNotification({ type: 'error', text: 'Preço inválido para ordem limitada' });
                    setLoading(false);
                    return;
                }
                order.limitPrice = parseFloat(priceValue);
            } else {
                // MARKET BUY: usa quoteOrderQty (valor em USDT/USDC) para a Binance
                // respeitar LOT_SIZE/stepSize sozinha — evita erro de "quantidade inválida".
                order.limitPrice = null;
                if (side === 'BUY') {
                    const px = parseFloat(price) || 0;
                    const quoteSpend = px > 0 ? parseFloat((orderQty * px).toFixed(2)) : 0;
                    if (quoteSpend > 0) {
                        order.quantity = null;
                        order.options.quoteOrderQty = quoteSpend;
                    }
                }
            }

            console.log('Enviando ordem:', JSON.stringify(order, null, 2));
            const result = await placeOrder(order);
            console.log('Resposta da ordem:', result);
            
            // Verifica diferentes formatos de resposta
            if (result) {
                if (result.id || result.orderId || result.status === 'NEW' || result.status === 'FILLED') {
                    const orderId = result.id || result.orderId || 'N/A';
                    setNotification({ 
                        type: 'success', 
                        text: `Ordem de ${side === 'BUY' ? 'compra' : 'venda'} criada com sucesso! ID: ${orderId}` 
                    });
                    setTimeout(() => {
                        navigate('/orders/' + symbol);
                    }, 1500);
                } else if (result.status === 'FAILED' || result.code) {
                    const errorMsg = result.msg || result.message || 'Ordem falhou';
                    setNotification({ type: 'error', text: errorMsg });
                    setError(errorMsg);
                } else {
                    // Resposta sem id mas também sem erro explícito
                    console.warn('Resposta inesperada da ordem:', result);
                    setNotification({ 
                        type: 'warning', 
                        text: `Ordem enviada, mas resposta inesperada. Verifique suas ordens.` 
                    });
                    setTimeout(() => {
                        navigate('/orders/' + symbol);
                    }, 2000);
                }
            } else {
                setNotification({ type: 'error', text: 'Resposta vazia do servidor' });
            }
        } catch (err) {
            console.error('Erro ao enviar ordem:', err);
            let errorMsg = 'Erro ao enviar ordem';
            
            try {
                if (err && err.response) {
                    const data = err.response.data;
                    if (data) {
                        if (typeof data === 'string') {
                            if (data.trim()) {
                                try {
                                    const parsed = JSON.parse(data);
                                    errorMsg = parsed.msg || parsed.message || parsed.error || data;
                                } catch (e) {
                                    errorMsg = data;
                                }
                            }
                        } else if (typeof data === 'object') {
                            errorMsg = data.msg || data.message || data.error || JSON.stringify(data);
                        }
                    } else if (err.response.status) {
                        errorMsg = `Erro ${err.response.status}: ${err.response.statusText || 'Erro na requisição'}`;
                    }
                } else if (err && err.message) {
                    errorMsg = err.message;
                } else if (typeof err === 'string') {
                    errorMsg = err;
                } else {
                    errorMsg = 'Erro desconhecido ao enviar ordem';
                }
            } catch (parseErr) {
                console.error('Erro ao processar mensagem de erro:', parseErr);
                errorMsg = 'Erro ao processar resposta do servidor';
            }
            
            // Garante que a mensagem não seja vazia ou undefined
            if (!errorMsg || errorMsg === 'undefined' || errorMsg.trim() === '') {
                errorMsg = 'Erro desconhecido ao enviar ordem. Verifique o console para mais detalhes.';
            }
            
            console.log('Mensagem de erro final:', errorMsg);
            setNotification({ type: 'error', text: errorMsg });
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="trade-container">
                    <div className="trade-header">
                        <h1>Spot Execução em Tempo Real</h1>
                    </div>

                    <div className="trade-content">
                        {/* Seleção de Criptomoeda */}
                        <div className="trade-card">
                            <div className="trade-card-header">
                                <h3>Selecione a Criptomoeda</h3>
                            </div>
                            <div className="trade-card-body">
                                <SelectSymbol 
                                    symbol={symbol} 
                                    onChange={onSymbolChange} 
                                    disabled={false}
                                    allowedSymbols={side === 'SELL' ? (availableSymbols.length > 0 ? availableSymbols : []) : undefined}
                                />
                                {side === 'SELL' && availableSymbols.length === 0 && (
                                    <div className="info-message">
                                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                        Carregando símbolos disponíveis...
                                    </div>
                                )}
                                {side === 'SELL' && availableSymbols.length > 0 && (
                                    <div className="info-message success">
                                        <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        Mostrando apenas pares que você possui ({availableSymbols.length} disponíveis)
                                    </div>
                                )}
                                {cryptoInfo && (
                                    <div className="crypto-info">
                                        <img 
                                            src={cryptoInfo.image} 
                                            alt={cryptoInfo.name}
                                            className="crypto-image"
                                            onError={(e) => {
                                                e.target.src = 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@bea1a9722a8c63169dcc06e86182bf2c55a76bbc/128/color/generic.png';
                                            }}
                                        />
                                        <div>
                                            <div className="crypto-name">{cryptoInfo.name}</div>
                                            <div className="crypto-price">${parseFloat(cryptoInfo.price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Botões Comprar/Vender */}
                        <div className="trade-card">
                            <div className="trade-card-header">
                                <h3>Operação</h3>
                            </div>
                            <div className="trade-card-body">
                                <div className="side-buttons">
                                    <button
                                        className={`side-button ${side === 'BUY' ? 'active buy' : ''}`}
                                        onClick={() => handleSideChange('BUY')}
                                    >
                                        <svg className="icon icon-lg me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                                        </svg>
                                        Comprar
                                    </button>
                                    <button
                                        className={`side-button ${side === 'SELL' ? 'active sell' : ''}`}
                                        onClick={() => handleSideChange('SELL')}
                                    >
                                        <svg className="icon icon-lg me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
                                        </svg>
                                        Vender
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Tipo de Ordem */}
                        <div className="trade-card">
                            <div className="trade-card-header">
                                <h3>Tipo de Ordem</h3>
                            </div>
                            <div className="trade-card-body">
                                <div className="order-type-buttons">
                                    <button
                                        className={`order-type-button ${orderType === 'MARKET' ? 'active' : ''}`}
                                        onClick={() => setOrderType('MARKET')}
                                    >
                                        Mercado
                                    </button>
                                    <button
                                        className={`order-type-button ${orderType === 'LIMIT' ? 'active' : ''}`}
                                        onClick={() => setOrderType('LIMIT')}
                                    >
                                        Limite
                                    </button>
                                </div>
                                {orderType === 'LIMIT' && (
                                    <div className="order-type-explanation">
                                        <div className="explanation-header">
                                            <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                            <strong>Quando usar Ordem Limite?</strong>
                                        </div>
                                        <div className="explanation-content">
                                            <p><strong>Ordem Limite</strong> permite que você defina o preço exato que deseja pagar (comprar) ou receber (vender).</p>
                                            <ul>
                                                <li><strong>Use para comprar:</strong> quando quiser pagar menos que o preço atual de mercado</li>
                                                <li><strong>Use para vender:</strong> quando quiser receber mais que o preço atual de mercado</li>
                                                <li><strong>Vantagem:</strong> você controla o preço, mas a ordem pode não ser executada imediatamente</li>
                                                <li><strong>Dica:</strong> a ordem só será executada quando o preço de mercado atingir o valor que você definiu</li>
                                            </ul>
                                        </div>
                                    </div>
                                )}
                                {orderType === 'MARKET' && (
                                    <div className="order-type-explanation">
                                        <div className="explanation-header">
                                            <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <strong>Ordem a Mercado</strong>
                                        </div>
                                        <div className="explanation-content">
                                            <p>A ordem será executada imediatamente pelo melhor preço disponível no mercado.</p>
                                            <p><strong>Ideal para:</strong> quando você precisa executar a ordem rapidamente e não se importa com pequenas variações de preço.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Preço e Quantidade */}
                        <div className="trade-card">
                            <div className="trade-card-header">
                                <h3>Detalhes da Ordem</h3>
                            </div>
                            <div className="trade-card-body">
                                {symbol && (
                                    <PriceDisplay symbol={symbol} onPriceChange={onPriceChange} />
                                )}
                                
                                {orderType === 'LIMIT' && (
                                    <div className="form-group">
                                        <label>Preço Limite ({symbolData?.quote || ''})</label>
                                        <input
                                            type="number"
                                            className="trade-input"
                                            placeholder="0.00"
                                            value={limitPrice}
                                            onChange={handleLimitPriceChange}
                                            step="any"
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label>Quantidade ({symbolData?.base || ''})</label>
                                    <input
                                        type="number"
                                        className="trade-input"
                                        placeholder="0.00"
                                        value={quantity}
                                        onChange={handleQuantityChange}
                                        step="any"
                                    />
                                    {wallet && (
                                        <div className="percentage-buttons">
                                            <button onClick={() => applyPercentage(25)} className="percentage-btn">25%</button>
                                            <button onClick={() => applyPercentage(50)} className="percentage-btn">50%</button>
                                            <button onClick={() => applyPercentage(75)} className="percentage-btn">75%</button>
                                            <button onClick={() => applyPercentage(100)} className="percentage-btn">100%</button>
                                        </div>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label>Total ({symbolData?.quote || ''})</label>
                                    <input
                                        type="text"
                                        className="trade-input"
                                        value={total}
                                        readOnly
                                        style={{ background: 'rgba(26, 26, 26, 0.5)' }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Saldo Disponível */}
                        {wallet && symbolData && (
                            <div className="trade-card">
                                <div className="trade-card-header">
                                    <h3>Saldo Disponível</h3>
                                </div>
                                <div className="trade-card-body">
                                    <div className="wallet-info">
                                        <div className="wallet-item">
                                            <span className="wallet-label">{symbolData.base}:</span>
                                            <span className="wallet-value">{parseFloat(wallet.base.qty || 0).toFixed(8)}</span>
                                        </div>
                                        <div className="wallet-item">
                                            <span className="wallet-label">{symbolData.quote}:</span>
                                            <span className="wallet-value">{parseFloat(wallet.quote.qty || 0).toFixed(8)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Botão de Confirmar */}
                        <div className="trade-card">
                            <div className="trade-card-body">
                                {error && (
                                    <div className="error-message">
                                        {error}
                                    </div>
                                )}
                                <button
                                    className={`trade-submit-button ${side === 'BUY' ? 'buy' : 'sell'}`}
                                    onClick={handleSubmit}
                                    disabled={loading || !!error || !symbol || !quantity}
                                >
                                    {loading ? 'Processando...' : `${side === 'BUY' ? 'Comprar' : 'Vender'} ${symbolData?.base || ''}`}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default Trade;

